import { zodResolver } from '@hookform/resolvers/zod';
import { Button, MenuItem, Stack, TextField } from '@mui/material';
import { BackgroundColors, PhaseType, zPhaseType } from '@strength/domain';
import { I18nTranslator } from '@strength/i18n';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Drawer, DrawerFooter, DrawerHeader } from '../../../../components/drawer';
import { Form, FormField, FormItem } from '../../../../components/form/form';
import { ColorInput } from '../../../../components/input/color-input';
import { StringDatePicker } from '../../../../components/input/string-date-picker';
import { ConfirmationModal } from '../../../../components/modals/confirmation-modal';
import {
  DeleteConfirmationModal,
  deletePhaseText,
} from '../../../../components/modals/delete-confirmation-modal';
import { cn } from '../../../../components/utils';
import { Phase } from '../types';

export const buildPhaseSchema = (t: I18nTranslator) =>
  z
    .object({
      name: z.string().min(3, t('phase.nameMin', { count: 3 })),
      type: zPhaseType.optional(),
      startDate: z
        .string()
        .min(1, t('phase.startDateRequired'))
        .refine(
          (d) => DateTime.fromISO(d).isValid,
          t('phase.invalidDateFormat'),
        ),
      endDate: z
        .string()
        .min(1, t('phase.endDateRequired'))
        .refine(
          (d) => DateTime.fromISO(d).isValid,
          t('phase.invalidDateFormat'),
        ),
      color: z.string(),
    })
    .refine(
      (data) => {
        const s = DateTime.fromISO(data.startDate);
        const e = DateTime.fromISO(data.endDate);
        if (!s.isValid || !e.isValid) return true;
        return e > s;
      },
      { path: ['endDate'], message: t('phase.endAfterStart') },
    );

const phaseOptions: Record<PhaseType, string> = {
  preparation: 'Preparation',
  'pre-season': 'Pre-Season',
  'in-season': 'In-Season',
  'post-season': 'Post-Season',
  transition: 'Transition',
  'off-season': 'Off-Season',
};

const getPhaseOptionLabel = (type: PhaseType, t: I18nTranslator): string => {
  const translations: Record<PhaseType, string> = {
    preparation: t('preparation'),
    'pre-season': t('preSeason'),
    'in-season': t('inSeason'),
    'post-season': t('postSeason'),
    transition: t('transition'),
    'off-season': t('offSeason'),
  };
  return translations[type];
};

export type PhaseForm = z.infer<ReturnType<typeof buildPhaseSchema>>;

export function PhaseSheet({
  existingPhases,
  open,
  isEdit,
  defaultValues,
  onSave,
  onClose,
  onDelete,
}: {
  existingPhases: Phase[];
  open: boolean;
  isEdit: boolean;
  defaultValues: Partial<Phase>;
  onSave: (form: PhaseForm) => void;
  onClose: () => void;
  onDelete?: () => void;
}) {
  const { t } = useTranslation();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const form = useForm<PhaseForm>({
    mode: 'onChange',
    resolver: zodResolver(buildPhaseSchema(t)),
    defaultValues: getValuesFromPhase(defaultValues),
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = form;

  const watchedStartDate = form.watch('startDate');
  const watchedEndDate = form.watch('endDate');

  const closeHandler = useCallback(() => {
    if (isDirty) {
      setCancelModalOpen(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const checkPhaseOverlap = useCallback(() => {
    // Require both dates and valid ISO parsing before checking overlap
    if (!watchedStartDate || !watchedEndDate) return false;

    const newStart = DateTime.fromISO(watchedStartDate);
    const newEnd = DateTime.fromISO(watchedEndDate);
    if (!newStart.isValid || !newEnd.isValid) return false;

    const currentId = defaultValues?.id;

    // Overlap test for closed intervals: [a1, a2] overlaps [b1, b2] iff a1 <= b2 && b1 <= a2
    return existingPhases.some((phase) => {
      // ignore self while editing
      if (currentId && phase.id === currentId) return false;

      const pStart = DateTime.fromISO(phase.startDate);
      const pEnd = DateTime.fromISO(phase.endDate);
      if (!pStart.isValid || !pEnd.isValid) return false;
      return newStart <= pEnd && pStart <= newEnd;
    });
  }, [existingPhases, watchedStartDate, watchedEndDate, defaultValues?.id]);

  const isPhaseOverlap = useMemo(() => {
    return checkPhaseOverlap();
  }, [checkPhaseOverlap]);

  return (
    <Drawer open={open} onClose={() => onClose()}>
      <DrawerHeader
        title={isEdit ? t('editPhase') : t('addPhase')}
        onClose={() => onClose()}
      />

      <Form {...form}>
        <form
          id="phase-form"
          onSubmit={handleSubmit(onSave)}
          className="gap-4 flex flex-col overflow-y-hidden flex-grow"
        >
          <div className="px-8 py-4 grow flex flex-col gap-4">
            <FormField
              name={'name'}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormItem>
                  <TextField
                    label={t('name')}
                    {...field}
                    fullWidth
                    error={Boolean(error)}
                    helperText={error?.message}
                  />
                </FormItem>
              )}
            />

            <div className="w-full flex flex-row space-x-3">
              <FormField
                name={'type'}
                control={control}
                render={({ field }) => (
                  <FormItem className="w-3/4">
                    <TextField
                      label={t('type')}
                      select
                      fullWidth
                      onChange={field.onChange}
                      value={field.value}
                    >
                      {Object.entries(phaseOptions).map(([key]) => (
                        <MenuItem key={key} value={key}>
                          {getPhaseOptionLabel(key as PhaseType, t)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormItem>
                )}
              />
              <FormField
                name={'color'}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormItem className="w-1/4">
                    <ColorInput onChange={onChange} value={value} />
                  </FormItem>
                )}
              />
            </div>

            <Stack direction="row" spacing={2}>
              <FormField
                name={'startDate'}
                control={control}
                render={({
                  field: { onChange, value, ref },
                  fieldState: { error },
                }) => (
                  <FormItem>
                    <StringDatePicker
                      label={t('startDate')}
                      onChange={onChange}
                      value={value}
                      inputRef={ref}
                      error={Boolean(error) || isPhaseOverlap}
                      errorMessage={
                        error?.message ||
                        (isPhaseOverlap ? t('phase.overlapWarning') : '')
                      }
                    />
                  </FormItem>
                )}
              />

              <FormField
                name={'endDate'}
                control={control}
                render={({
                  field: { onChange, value, ref },
                  fieldState: { error },
                }) => (
                  <FormItem>
                    <StringDatePicker
                      label={t('endDate')}
                      onChange={onChange}
                      value={value}
                      inputRef={ref}
                      error={Boolean(error)}
                      errorMessage={error?.message}
                      minDate={
                        watchedStartDate
                          ? DateTime.fromISO(watchedStartDate).plus({
                              days: 1,
                            })
                          : undefined
                      }
                    />
                  </FormItem>
                )}
              />
            </Stack>
          </div>
        </form>
      </Form>
      <DrawerFooter
        className={cn(
          'flex flex-row w-full p-8',
          isEdit && !!onDelete ? 'justify-between' : 'justify-end',
        )}
      >
        {isEdit && !!onDelete && (
          <>
            <Button
              variant="containedDestructive"
              onClick={() => setDeleteModalOpen(true)}
            >
              {t('delete')}
            </Button>
            <div className="flex-grow" />
          </>
        )}

        <div className="space-x-2">
          <Button variant="outlined" onClick={closeHandler}>
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            form="phase-form"
            disabled={!form.formState.isValid || isPhaseOverlap}
          >
            {t('save')}
          </Button>
        </div>
      </DrawerFooter>
      <DeleteConfirmationModal
        id={defaultValues?.id ?? ''}
        open={deleteModalOpen}
        {...deletePhaseText(t)}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={() => onDelete?.()}
      />
      <ConfirmationModal
        id={defaultValues?.id ?? ''}
        open={cancelModalOpen}
        title={`Cancel Phase ${isEdit ? 'Edit' : 'Add'}`}
        message={`Are you sure you want to cancel ${
          isEdit ? 'editing' : 'adding'
        } this phase? All changes will be lost.`}
        onConfirm={() => {
          setCancelModalOpen(false);
          onClose();
        }}
        onCancel={() => setCancelModalOpen(false)}
      />
    </Drawer>
  );
}

export const getValuesFromPhase = (phase: Partial<Phase>): PhaseForm => {
  return {
    name: phase.name || '',
    type: phase.type || undefined,
    color: phase.color || BackgroundColors[0] || '',
    startDate: phase.startDate || '',
    endDate: phase.endDate || '',
  };
};
